import { Controller } from '@hotwired/stimulus'
import {
  computePosition,
  flip,
  shift,
  offset,
  arrow,
  autoUpdate
} from '@floating-ui/dom'
import { cachedFetch } from '../utils/cached_fetch'

const fetchRemoteContent = cachedFetch({
  maxAge: 5 * 60 * 1000,
  init: { headers: { Accept: 'text/html' } },
  read: response => response.text()
})

/**
 * @controller floating-tooltip
 * @description Initializes Floating UI tooltips on specified source elements,
 *              using content from each source's `data-tooltip-content` attribute.
 *              Placement can be set globally via `data-placement-value` on the controller 
 *              or overridden per element via `data-placement-value` on the source target.
 * @targets source
 * @values placement (String, optional, default: 'top'), 
 *         theme (String, optional, default: 'light'),
 *         formatMarkdown (Boolean, optional, default: false),
 *         tooltipClass (String, optional, default: '')
 * @example
 *   <div 
 *     data-controller="floating-tooltip" 
 *     data-placement-value="bottom"
 *     data-floating-tooltip-format-markdown-value="true"
 *     data-floating-tooltip-tooltip-class-value="custom-tooltip-class"
 *   >
 *     <a 
 *       href="#" 
 *       data-floating-tooltip-target="source" 
 *       data-tooltip-content="Content for link 1"
 *     >Hover me</a>
 *     <button 
 *       data-floating-tooltip-target="source"
 *       data-tooltip-content="Content for button 2"
 *       data-placement-value="top" 
 *     >Hover me too</button>
 *   </div>
 */
export default class extends Controller {
  static targets = ["source"]
  static values = {
    placement: { type: String, default: 'top' },
    theme: { type: String, default: 'light' },
    formatMarkdown: { type: Boolean, default: false },
    tooltipClass: { type: String, default: '' },
    showDelay: { type: Number, default: 0 }
  }

  connect() {
    this._initializeTooltips(this.sourceTargets)
  }

  sourceTargetConnected(element) {
    this._initializeTooltips([element])
  }

  sourceTargetDisconnected(element) {
    this._destroyTooltip(element)
  }

  disconnect() {
    this.sourceTargets.forEach(el => this._destroyTooltip(el))
  }

  // Private methods

  _initializeTooltips(elements) {
    elements.forEach(el => {
      if (el._floatingTooltip) return

      const content = el.dataset.tooltipContent ?? (el.dataset.tooltipUrl ? '' : undefined)
      if (content === undefined || content === null) {
        console.warn('Floating tooltip source element missing data-tooltip-content or data-tooltip-url:', el)
        return
      }

      // Create tooltip element
      const tooltip = document.createElement('div')
      const isLongContent = el.dataset.longContent === 'true'
      const baseClasses = `floating-tooltip floating-tooltip--${this.themeValue} formatted-text ${isLongContent ? 'floating-tooltip--long-content' : 'floating-tooltip--compact'}`
      const customClass = this.tooltipClassValue || ''
      tooltip.className = `${baseClasses} ${customClass}`.trim()
      tooltip.innerHTML = content // Use innerHTML since content might contain HTML from format_markdown
      tooltip.style.position = 'absolute'
      tooltip.style.display = 'none'
      tooltip.style.opacity = '0'
      document.body.appendChild(tooltip)

      // Create arrow element
      const arrowEl = document.createElement('div')
      arrowEl.className = 'floating-tooltip-arrow'
      tooltip.appendChild(arrowEl)

      // Store references
      el._floatingTooltip = tooltip
      el._floatingTooltipArrow = arrowEl
      el._hideTimeout = null
      el._hideFallbackTimeout = null
      el._floatingTooltipHideAfterTransition = null

      // Make tooltip clicks trigger the same action as clicking the source element
      tooltip.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        // Trigger the click on the original element so Stimulus actions fire
        if (typeof el.click === 'function') el.click()
        this._hideTooltip(el)
      })

      // Setup event listeners for the trigger element
      el.addEventListener('mouseenter', () => {
        this._loadRemoteContent(el)
        clearTimeout(el._hideTimeout)
        clearTimeout(el._showTimeout)
        const delay = this.showDelayValue
        if (delay > 0) {
          el._showTimeout = setTimeout(() => this._showTooltip(el), delay)
        } else {
          this._showTooltip(el)
        }
      })
      
      el.addEventListener('mouseleave', (e) => {
        clearTimeout(el._showTimeout)
        // Check if we're moving to the tooltip
        const toElement = e.relatedTarget
        if (toElement === tooltip || tooltip.contains(toElement)) {
          return
        }

        el._hideTimeout = setTimeout(() => {
          this._hideTooltip(el)
        }, 100) // Small delay to allow moving to tooltip
      })

      // Setup event listeners for the tooltip itself
      tooltip.addEventListener('mouseenter', () => {
        this._cancelPendingHide(el)
        tooltip.style.opacity = '1'
      })

      tooltip.addEventListener('mouseleave', (e) => {
        // Check if we're moving back to the trigger
        const toElement = e.relatedTarget
        if (toElement === el || el.contains(toElement)) {
          return
        }
        
        el._hideTimeout = setTimeout(() => {
          this._hideTooltip(el)
        }, 100)
      })

      // Focus events
      el.addEventListener('focus', () => {
        clearTimeout(el._hideTimeout)
        this._showTooltip(el)
      })
      
      el.addEventListener('blur', (e) => {
        // Check if we're moving to the tooltip
        const toElement = e.relatedTarget
        if (toElement === tooltip || tooltip.contains(toElement)) {
          return
        }
        
        el._hideTimeout = setTimeout(() => {
          this._hideTooltip(el)
        }, 100)
      })
    })
  }

  _loadRemoteContent(element) {
    const url = element.dataset.tooltipUrl
    if (!url) return null

    element._floatingTooltipRemoteRequest ??= fetchRemoteContent(url)
      .then(html => {
        element._floatingTooltip?.insertAdjacentHTML('afterbegin', html)
        return true
      })
      .catch(() => {
        delete element._floatingTooltipRemoteRequest
        return false
      })
    return element._floatingTooltipRemoteRequest
  }

  async _showTooltip(element) {
    const tooltip = element._floatingTooltip
    const arrowEl = element._floatingTooltipArrow
    this._cancelPendingHide(element)

    if (element.dataset.tooltipUrl) {
      const showRequest = element._floatingTooltipShowRequest = {}
      const loaded = await this._loadRemoteContent(element)
      if (!loaded || element._floatingTooltipShowRequest !== showRequest || !element._floatingTooltip) return
    }

    if (element._floatingTooltipCleanup) {
      element._floatingTooltipCleanup()
      element._floatingTooltipCleanup = null
    }

    // Make visible and fade in
    tooltip.style.display = 'block'
    // Force reflow so the transition applies
    // eslint-disable-next-line no-unused-expressions
    tooltip.offsetHeight
    tooltip.style.opacity = '1'

    const elementPlacement = element.dataset.placementValue
    const placement = elementPlacement !== undefined ? elementPlacement : this.placementValue

    // Use a custom reference element for width and positioning if specified
    const referenceSelector = element.dataset.tooltipReferenceSelector
    const widthElement = referenceSelector ? document.querySelector(referenceSelector) || element : null
    // Position relative to the width element, falling back to the trigger element
    const referenceElement = widthElement || element

    // Match the tooltip width to the reference element when using a custom reference
    if (widthElement && widthElement !== element) {
      tooltip.style.width = `${widthElement.offsetWidth}px`
      tooltip.style.maxWidth = 'none'
    }

    // Update position
    const cleanup = autoUpdate(referenceElement, tooltip, () => {
      computePosition(referenceElement, tooltip, {
        placement,
        middleware: [
          offset(8),
          flip(),
          shift({ padding: 5 }),
          arrow({ element: arrowEl })
        ]
      }).then(({ x, y, placement, middlewareData }) => {
        Object.assign(tooltip.style, {
          left: `${x}px`,
          top: `${y}px`
        })

        // Position arrow
        const { x: arrowX, y: arrowY } = middlewareData.arrow
        Object.assign(arrowEl.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [placement.split('-')[0]]: '100%'
        })
      })
    })

    // Store cleanup function
    element._floatingTooltipCleanup = cleanup
  }

  _hideTooltip(element) {
    element._floatingTooltipShowRequest = null
    const tooltip = element._floatingTooltip
    if (tooltip) {
      this._cancelPendingHide(element)

      // Fade out, then hide after transition
      tooltip.style.opacity = '0'
      const hideAfter = () => {
        if (tooltip.style.opacity !== '0') return

        tooltip.style.display = 'none'
        tooltip.removeEventListener('transitionend', hideAfter)
        clearTimeout(element._hideFallbackTimeout)
        element._hideFallbackTimeout = null
        element._floatingTooltipHideAfterTransition = null
      }
      element._floatingTooltipHideAfterTransition = hideAfter
      tooltip.addEventListener('transitionend', hideAfter)
      element._hideFallbackTimeout = setTimeout(hideAfter, 180)
    }
    if (element._floatingTooltipCleanup) {
      element._floatingTooltipCleanup()
      element._floatingTooltipCleanup = null
    }
  }

  _cancelPendingHide(element) {
    clearTimeout(element._hideTimeout)
    clearTimeout(element._hideFallbackTimeout)
    element._hideTimeout = null
    element._hideFallbackTimeout = null

    const tooltip = element._floatingTooltip
    const hideAfter = element._floatingTooltipHideAfterTransition
    if (tooltip && hideAfter) {
      tooltip.removeEventListener('transitionend', hideAfter)
      element._floatingTooltipHideAfterTransition = null
    }
  }

  _destroyTooltip(element) {
    clearTimeout(element._showTimeout)
    this._cancelPendingHide(element)
    if (element._floatingTooltipCleanup) {
      element._floatingTooltipCleanup()
      element._floatingTooltipCleanup = null
    }

    if (element._floatingTooltip) {
      element._floatingTooltip.remove()
      delete element._floatingTooltip
      delete element._floatingTooltipArrow
      delete element._floatingTooltipCleanup
      delete element._hideTimeout
      delete element._hideFallbackTimeout
      delete element._floatingTooltipHideAfterTransition
      delete element._floatingTooltipRemoteRequest
      delete element._floatingTooltipShowRequest
    }
  }
}
